// Styles
import '../../MapStyles/MapMenu.scss';

// React
import { useEffect, useRef, useState } from 'react';

// React Bootstrap
import { Card, Form } from 'react-bootstrap';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';

// Services
import { Api } from '@services';

// Third-party Libraries
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

// Components
import { Icon, Text } from '@components';

// Store
import { useMapStore } from '../../MapStore/UseMapStore';

export function MapCreateStationary({
  curAssets,
  selectedAsset,
  setCurAssets,
  setSelectedAsset,
  setIsCreating,
}) {
  const setNewObjectId = useMapStore((state) => state.setNewObjId);
  const setNewObjName = useMapStore((state) => state.setNewObjName);
  const setNewObjFloor = useMapStore((state) => state.setNewObjFloor);
  const setNewObjKeywords = useMapStore((state) => state.setNewObjKeywords);
  const setNewObjOnline = useMapStore((state) => state.setNewObjOnline);
  const setCreateStationary = useMapStore((state) => state.setCreateStationary);
  const addObject = useMapStore((state) => state.addObject);
  const removeTempItem = useMapStore((state) => state.removeTempObject);
  const selected = useMapStore((state) => state.selected);
  const newObjCreated = useMapStore((state) => state.newObjCreated);

  const [name, setName] = useState<string>('');
  const [floor, setFloor] = useState<string>('1');
  const [id, setId] = useState<string>(null);
  const [isOnline, setIsOnline] = useState<boolean>(null);
  const [showSave, setShowSave] = useState<boolean>(false);
  const [keywords, setKeywords] = useState<any>([]);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [cache, setCache] = useState({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<any[]>([]);
  const [query, setQuery] = useState<string>('');
  const [filterAssigned, setFilterAssigned] = useState(false);
  const assetRef = useRef(null);
  const cancelToken = Api.CancelToken;
  let fetchDataCancel;

  const MySwal = withReactContent(Swal);

  useEffect(() => {
    setNewObjectId(id);
  }, [id, setNewObjectId]);

  useEffect(() => {
    setNewObjName(name);
  }, [name, setNewObjName]);

  useEffect(() => {
    setNewObjFloor(floor);
  }, [floor, setNewObjFloor]);

  useEffect(() => {
    setNewObjKeywords(keywords);
  }, [keywords, setNewObjKeywords]);

  useEffect(() => {
    setNewObjOnline(isOnline);
  }, [isOnline, setNewObjOnline]);

  useEffect(() => {
    newObjCreated !== null ? setShowSave(true) : setShowSave(false);
  }, [newObjCreated]);

  const handleInput = (items) => {
    if (items.length > 0) {
      if (items[0].assigned) {
        MySwal.fire({
          allowOutsideClick: false,
          title: 'You are reassigning this Asset',
          text: 'Are you sure you want to remove this Asset from its current map?',
          icon: 'warning',
          showCancelButton: true,
          cancelButtonText: 'No',
          confirmButtonText: 'Yes',
        }).then((submit) => {
          if (submit.isConfirmed) {
            setSelectedAsset(items[0].id);
            setId(items[0].id.toString());
            setName(items[0].label);
            setIsOnline(items[0].isOnline);
            setIsInvalid(false);
            setTimeout(() => {
              assetRef.current.blur();
            }, 500);
          } else {
            MySwal.close();
            assetRef.current.clear();
          }
        });
      } else {
        setSelectedAsset(items[0].id);
        setId(items[0].id.toString());
        setName(items[0].label);
        setIsOnline(items[0].isOnline);
        setIsInvalid(false);
      }
    } else {
      setSelectedAsset(null);
      setId('');
      setName('');
      setIsOnline(null);
      setIsInvalid(true);
    }
  };

  // Handle submission of item
  function startDraw() {
    setCreateStationary(true);
  }

  const getResults = async (search: string, pageNumber: number, pageSize: number = 30) => {
    const call = `v2/stationary-assets?search=${search}&pageSize=${pageSize}&pageNumber=${pageNumber}`;
    const resp = await Api.get<any[]>(call);

    const newItems = resp.data['data'].map((i) => ({
      id: i.id,
      name: i.name,
      isOnline: i.isOnline,
      assignedMap: i.assignedMap ? i.assignedMap.id : null,
    }));

    const next = resp.data['hasNextPage'];
    const total = resp.data['totalCount'];

    return { newItems, next, total, pageNumber };
  };

  const handleSearch = (value) => {
    setQuery(value);

    if (cache[value]) {
      setOptions(
        filterAssigned
          ? cache[value].items.filter((asset) => asset.assignedMap === null)
          : cache[value].items,
      );
    } else {
      setIsLoading(true);
      getResults(value, 1).then((resp) => {
        setOptions(
          filterAssigned
            ? resp.newItems.filter((asset) => asset.assignedMap === null)
            : resp.newItems,
        );
        setCache({
          ...cache,
          [value]: {
            items: resp.newItems,
            next: resp.next,
            total: resp.total,
            pageNumber: resp.pageNumber,
          },
        });
        setIsLoading(false);
      });
    }
  };

  const handlePagination = () => {
    const cached = cache[query];

    if (cached.items.length !== cached.total) {
      setIsLoading(true);
      const nextPage = cached.pageNumber + 1;

      getResults(query, nextPage).then((resp) => {
        const items = cached.items.concat(resp.newItems);

        setOptions(items);
        setCache({
          ...cache,
          [query]: {
            items,
            next: resp.next,
            total: resp.total,
            pageNumber: nextPage,
          },
        });
        setIsLoading(false);
      });
    }
  };

  const handleClick = () => {
    setCurAssets([...curAssets, selectedAsset]);
    addObject('Stationary');
    setIsCreating(false);
    setCreateStationary(false);
    setShowSave(false);
  };

  return (
    <Form className={selected !== 'stationary' ? 'object-wrapper' : 'object-wrapper-expand'}>
      <Card.Body>
        <div className="d-flex flex-column justify-content-between">
          <div className="d-flex flex-row align-items-center justify-content-between">
            <Text className="fw-semibold fs-md">Asset Search:</Text>
            <div>
              {showSave === true ? (
                <Icon
                  name={'check'}
                  title="Confirm"
                  className="align-sub primary-icon"
                  variant="light"
                  disabled={name === '' ? true : false}
                  size="md"
                  onClick={handleClick}
                />
              ) : (
                <Icon
                  name={'pin-edit'}
                  title="Place Stationary Asset"
                  className="align-sub primary-icon"
                  variant="light"
                  disabled={name === '' ? true : false}
                  size="md"
                  onClick={() => {
                    startDraw();
                  }}
                />
              )}

              <Icon
                name={'add'}
                title="Close"
                className="sonarIcon-rotate-45 align-sub nav-text-icon"
                variant="light"
                disabled={false}
                size="md"
                onClick={() => {
                  setCreateStationary(false);
                  setIsCreating(false);
                  removeTempItem();
                }}
              />
            </div>
          </div>
          <div className="d-flex flex-column align-items-left justify-content-between mb-3 qw-100">
            <AsyncTypeahead
              className="mb-3 w-100"
              ref={assetRef}
              allowNew={false}
              filterBy={['label', 'id']}
              delay={500}
              id="stationary"
              maxResults={10}
              minLength={1}
              isInvalid={isInvalid}
              isLoading={isLoading}
              onChange={(e) => {
                handleInput(e);
              }}
              onPaginate={handlePagination}
              onSearch={handleSearch}
              options={
                options &&
                options.map((option) => {
                  return {
                    id: option.id,
                    label: option.name,
                    isOnline: option.isOnline,
                    disabled: curAssets.includes(option.id),
                    assignedCur: curAssets.includes(option.id),
                    assigned: option.assignedMap,
                  };
                })
              }
              placeholder="Search for an Asset..."
              renderMenuItemChildren={(option, { text }) => (
                <div
                  title={option.label}
                  style={{ opacity: curAssets.includes(option.id) ? 0.5 : 1 }}
                >
                  <Highlighter search={text}>{option.label}</Highlighter>
                  <div className="d-flex flex-column">
                    <Text className="fs-xs">
                      Online:{' '}
                      {option.isOnline.toString().charAt(0).toUpperCase() +
                        option.isOnline.toString().slice(1)}
                    </Text>
                    {option.assigned && (
                      <div className="d-flex flex-row">
                        <Text className="fs-xs warn-text">Assigned to Map: &nbsp;</Text>
                        <Text className="fs-xs primary-text">{option.assigned}</Text>
                      </div>
                    )}
                    {option.assignedCur && (
                      <Text className="fs-xs">{'(Asset is already assigned to this Map)'}</Text>
                    )}
                  </div>
                </div>
              )}
              useCache={false}
            />
            <Form.Check
              label="Filter Assigned Assets"
              id={'filter-assigned-beacons-checkbox'}
              checked={filterAssigned}
              onChange={(e) => setFilterAssigned(e.target.checked)}
            />
          </div>
        </div>
      </Card.Body>
    </Form>
  );
}
